/**
 * src/features/sync/lib/firstSync.ts
 *
 * SYNC-03 (Phase 36 plan 06) — repairs D1 project rows written through the
 * pre-Phase-35 stripping contract, using the intact local IndexedDB copy as the
 * repair source (the named acceptance item inherited from 35-CONTEXT.md
 * Decision 3.4: the first sync per project must be a full blind upload of the
 * complete local snapshot, never a delta).
 *
 * WHY A NEW IDB KEY: this confirmed-set is a PER-PROJECT guarantee — "has this
 * specific project ever received a full snapshot from this device" — while the
 * existing guest→cloud migration flow (src/stores/migrate.ts) tracks a
 * PER-ACCOUNT guarantee and deletes its own per-project id-tracking set the
 * moment the account-level migrated flag flips true. Reusing that set would
 * silently disable this per-project repair guarantee the instant migration
 * completes, so this module declares its own independent key that outlives
 * that event.
 *
 * SELECTION RULE (option-b, operator-selected at the Task 1 decision
 * checkpoint in 36-06-PLAN.md): before any destructive write, GET the server
 * row. A 404 (never synced from any device) or a 200 whose global layer
 * (`projectItems` + `projectOverrides`) is empty while the local copy's is not
 * — the exact pre-Phase-35 stripping signature — triggers a blind PUT with
 * `baseVersion: null`, mirroring `migrateLocalToCloud`'s loop shape verbatim.
 * Any other 200 means the server row is already intact: the project is marked
 * confirmed WITHOUT a write, so a stale local copy can never clobber newer
 * state written by another device — the mirror-image overwrite risk the
 * checkpoint decision was gated on. A non-2xx response other than 404, a
 * thrown network error, or a response failing schema validation all mean the
 * server state is unknown; the safe default (never write when unknown) leaves
 * the project unconfirmed for the next login, matching
 * `migrateLocalToCloud`'s own never-PUT-when-unknown rule.
 */
import { get, update } from 'idb-keyval';
import { getProjectLibrary } from '@/entities/estimate/lib/project';
import { useSyncStore } from '@/stores/useSyncStore';
import { ProjectDetailResponseSchema } from '@/shared/validation/schemas';
import type { ProjectSnapshot } from '@/entities/estimate/model/types';

/** Distinct from the account-level guest-migration tracking key — see module header. */
const FIRST_SYNC_CONFIRMED_KEY = 'estimatix-first-sync-confirmed';

async function markConfirmed(projectId: string): Promise<void> {
  // Atomic read-modify-write (WR-03 precedent from migrate.ts) — concurrent
  // logins/tabs racing on this key must never let one writer's confirmed id
  // clobber another's.
  await update<string[]>(FIRST_SYNC_CONFIRMED_KEY, (existing) => {
    const s = new Set(existing ?? []);
    s.add(projectId);
    return [...s];
  });
}

/**
 * Performs the blind repair PUT for one project, mirroring
 * migrateLocalToCloud's PUT loop verbatim in shape.
 *
 * CR-02 fix (36-REVIEW.md): the damage signature that gates this call only
 * inspects the project-global item layer, but the write itself replaces the
 * ENTIRE snapshot (rooms included — see functions/lib/db.ts upsertProject,
 * which JSON.stringifies the whole snapshot unconditionally). When the
 * caller has determined the server row already has real committed rooms
 * (from ANY device), it passes them as `serverRoomsForRepair` so this write
 * carries them through untouched instead of overwriting them with the local
 * copy's rooms — narrowing what the blind repair can destroy without
 * widening the "healthy, no write" trigger (per 36-CONTEXT.md's option-b
 * decision, which this fix does not re-litigate).
 */
async function repairPut(
  project: ProjectSnapshot,
  serverRoomsForRepair?: ProjectSnapshot['rooms'],
): Promise<'uploaded' | 'conflict' | 'error'> {
  const projectId = String(project.id);
  try {
    const payload = serverRoomsForRepair
      ? { ...project, rooms: serverRoomsForRepair, baseVersion: null }
      : { ...project, baseVersion: null };
    const res = await fetch(`/api/projects/${projectId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(10_000),
      // The repair write is always a full blind upload of the complete local
      // snapshot, not a delta — baseVersion explicitly null tells the server
      // this is a blind upsert. `rooms` is overridden above when the server
      // already has real rooms to protect (CR-02).
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      await markConfirmed(projectId);
      return 'uploaded';
    }

    if (res.status === 409) {
      // Server wins (server-authoritative OCC) — do NOT mark confirmed, do NOT
      // re-PUT in this run, do NOT count as a retryable error (T-11-09).
      try {
        await res.json();
      } catch {
        // Malformed/empty 409 body — still a conflict regardless of body shape.
      }
      useSyncStore.getState().setStatus('conflict');
      return 'conflict';
    }

    // Any other non-2xx (e.g. 500) — leave unconfirmed for the next login.
    return 'error';
  } catch {
    // Network error or timeout — leave unconfirmed for the next login.
    return 'error';
  }
}

/**
 * Repairs projects whose D1 row was written through the pre-Phase-35
 * stripping contract, using the local library as the intact repair source.
 * Never gates on the account-level guest-migration flag — that is a
 * per-account guarantee, this is a per-project one.
 */
export async function ensureFirstSyncUploaded(): Promise<{
  uploaded: number;
  conflicts: number;
  errors: number;
}> {
  let uploaded = 0;
  let conflicts = 0;
  let errors = 0;

  const library = await getProjectLibrary();
  if (library.length === 0) {
    return { uploaded, conflicts, errors };
  }

  const confirmedIds = (await get<string[]>(FIRST_SYNC_CONFIRMED_KEY)) ?? [];
  const confirmedSet = new Set(confirmedIds);

  for (const project of library) {
    const projectId = String(project.id);
    if (confirmedSet.has(projectId)) continue; // Zero network requests.

    // CR-02 fix: when the damage signature matches below but the server row
    // already has real rooms (committed by any device — the item-layer
    // stripping bug never touched rooms), the repair write must carry those
    // rooms through untouched rather than overwrite them with the local
    // copy's rooms. Only a 404 (never synced by ANY device — no server rooms
    // exist to protect) leaves this undefined, matching the original blind
    // full-upload behavior for that case.
    let serverRoomsForRepair: ProjectSnapshot['rooms'] | undefined;

    try {
      const getRes = await fetch(`/api/projects/${projectId}`, {
        method: 'GET',
        signal: AbortSignal.timeout(10_000),
      });

      if (getRes.status !== 404) {
        if (!getRes.ok) {
          // Non-2xx other than 404 — server state unknown, never write.
          errors++;
          continue;
        }

        const { project: serverProject } = ProjectDetailResponseSchema.parse(await getRes.json());
        const serverData = serverProject.data;
        const serverGlobalLayerEmpty =
          serverData.projectItems.length === 0 && Object.keys(serverData.projectOverrides).length === 0;
        const localGlobalLayerNonEmpty =
          (project.projectItems ?? []).length > 0 || Object.keys(project.projectOverrides ?? {}).length > 0;

        if (!(serverGlobalLayerEmpty && localGlobalLayerNonEmpty)) {
          // Server row is intact — confirm WITHOUT a write. This is the branch
          // that protects newer server state written by another device from a
          // stale local copy.
          await markConfirmed(projectId);
          continue;
        }

        // Damage signature matched — the item layer needs repair. If the
        // server already has real rooms, capture them so the repair PUT
        // below preserves them instead of overwriting with local rooms.
        // ProjectSnapshotSchema's z.infer (Worker/transport shape — e.g.
        // Opening.width as string|number) and the client's hand-authored
        // ProjectSnapshot interface (entities/estimate/model/types.ts —
        // Opening.width as number) are two distinct types for historical
        // reasons; bridged with the same `as` cast hydrate.ts and
        // useSyncStore.ts already use for this identical gap.
        if (serverData.rooms.length > 0) {
          serverRoomsForRepair = serverData.rooms as ProjectSnapshot['rooms'];
        }
      }
      // Falls through only for a 404 or the pre-Phase-35 damage signature —
      // both cases perform the blind repair PUT below.
    } catch {
      // Network error or a response failing ProjectDetailResponseSchema —
      // server state unknown, never write.
      errors++;
      continue;
    }

    const outcome = await repairPut(project, serverRoomsForRepair);
    if (outcome === 'uploaded') uploaded++;
    else if (outcome === 'conflict') conflicts++;
    else errors++;
  }

  return { uploaded, conflicts, errors };
}
